import {merge} from 'lodash';
import Assessment from '../assessment';
import AssessmentResult from '../../../values/AssessmentResult';
import {META_TAG_POINTS, TITLE_KEYWORD_ID} from '@axyseo/const/analysis';

/**
 * Assessment for checking the keyword matches in the meta title.
 */
export default class MetaTitleKeywordAssessment extends Assessment {
  /**
   * Sets the identifier and the config.
   *
   * @param {Object} [config] The configuration to use.
   * @param {number} [config.parameters.recommendedMinimum] The recommended minimum of keyword occurrences in the meta description.
   * @param {number} [config.scores.good] The score to return if there are enough keyword occurrences in the meta description.
   * @param {number} [config.scores.bad] The score to return if there aren't enough keyword occurrences in the meta description.
   * @param {string} [config.url] The URL to the relevant article on Yoast.com.
   *
   * @returns {void}
   */
  constructor(config = {}) {
    super();

    const defaultConfig = {
      id: TITLE_KEYWORD_ID,
      docUrl:
        'https://docs.avada.io/seo-suite-help-center/seo-audit/on-page-seo/checklist#keyword-in-title',
      fixPosition: 'meta title',
      ctaType: 'fix',
      priority: 'high',
      title: 'Keyword in meta title',
      content: {
        bad:
          'No keywords found in meta title. Add one to improve keyword rank and click through rate.',
        good: 'Keyword is included in meta title.',
        improve: ''
      }
    };

    this.identifier = TITLE_KEYWORD_ID;
    this._config = merge(defaultConfig, config);
  }

  /**
   * Runs the metaDescriptionKeyword researcher and based on this, returns an assessment result with score.
   *
   * @param {Paper}      paper      The paper to use for the assessment.
   * @param {Researcher} researcher The researcher used for calling research.
   *
   * @returns {AssessmentResult} The assessment result.
   */
  getResult({paper, researcher}) {
    this._keyphraseMatches = researcher.getResearch('findKeyphraseInSEOTitle');

    const assessmentResult = new AssessmentResult({
      config: this._config
    });
    const calculatedResult = this.calculateResult();

    assessmentResult.setScore(calculatedResult.score);
    assessmentResult.setStatus(calculatedResult.status);

    return assessmentResult;
  }

  /**
   *
   * @returns {{score: number, status: string}}
   */
  calculateResult() {
    const exactMatchFound = this._keyphraseMatches.exactMatchFound;
    const allWordsFound = this._keyphraseMatches.allWordsFound;
    const exactMatchKeyphrase = this._keyphraseMatches.exactMatchKeyphrase;

    let status = 'bad';
    if (exactMatchFound === true || allWordsFound) {
      status = 'good';
    }
    if (exactMatchKeyphrase) {
      status = 'bad';
    }

    const score = this.getScore(META_TAG_POINTS, status);

    return {
      score,
      status
    };
  }

  /**
   * Checks whether the paper has a keyword and a meta description.
   *
   * @param {Paper} paper The paper to use for the assessment.
   *
   * @returns {boolean} True if the paper has a keyword and a meta description.
   */
  isApplicable(paper) {
    return true;
  }
}
